import AuthorizationStep from './steps/AuthorizationStep';
import ProjectLinkStep from './steps/ProjectLinkStep';
import GeneralDisclaimerStep from './steps/GeneralDisclaimerStep';
import AcknowledgementStep from './steps/AcknowledgementStep';
import FileUploadStep from './steps/FileUploadStep';
import BaseFeesPaymentStep from './steps/BaseFeesPaymentStep';
import { getGuestId } from '@/utils/getGuestId';

// Ordered list of all possible steps. The backend decides which are required.
export const DEFAULT_STEP_DEFINITIONS = [
  { key: 'authorization', label: 'Authorization', order: 1 },
  { key: 'project_link', label: 'Project Link', order: 2 },
  { key: 'general_disclaimer', label: 'General Disclaimer', order: 3 },
  { key: 'acknowledgement', label: 'Acknowledgement', order: 4 },
  { key: 'file_upload', label: 'File Upload', order: 5 },
  { key: 'base_fees_payment', label: 'Base Fees Payment', order: 6 },
];

// Maps each step key to its rendered component.
export const STEP_COMPONENTS = {
  authorization: AuthorizationStep,
  project_link: ProjectLinkStep,
  general_disclaimer: GeneralDisclaimerStep,
  acknowledgement: AcknowledgementStep,
  file_upload: FileUploadStep,
  base_fees_payment: BaseFeesPaymentStep,
};

// Title and subtitle shown in the modal header for each step.
export const STEP_META = {
  authorization: {
    title: 'Authorization Required',
    subtitle: 'For security purposes, please verify your identity to continue.',
  },
  project_link: {
    title: 'Link Your Project',
    subtitle: 'Connect this permit application to a project.',
  },
  general_disclaimer: {
    title: 'General Disclaimer',
    subtitle: 'Please read and accept the disclaimer to continue.',
  },
  acknowledgement: {
    title: 'Acknowledgements',
    subtitle: 'Please review and acknowledge all items below.',
  },
  file_upload: {
    title: 'Upload Documents',
    subtitle: 'Upload the required documents for your permit application.',
  },
  base_fees_payment: {
    title: 'Process Your Payment',
    subtitle: 'Please choose the payment method for the Zermit service fee to process and submit your application.',
  },
};

/**
 * Builds the active step list from backend data.
 * Falls back to all default steps when no backend data is provided.
 * Only steps marked required:true are included when backend data is present.
 */
export function buildSteps(backendSteps = []) {
  if (!backendSteps.length) {
    return [...DEFAULT_STEP_DEFINITIONS].sort((a, b) => a.order - b.order);
  }

  const backendMap = new Map(backendSteps.map(s => [s.key, s]));

  return DEFAULT_STEP_DEFINITIONS.filter(s => backendMap.has(s.key))
    .map(s => ({ ...s, ...backendMap.get(s.key) }))
    .filter(s => s.required === true)
    .sort((a, b) => a.order - b.order);
}

/** Shapes message metadata into the submitPermit API payload. */
export function buildSubmitPayload(metadata = {}) {
  return {
    user_id: getGuestId(),
    application_id: null,
    scope_of_work: metadata.scope_of_work || null,
    jurisdiction_code: metadata.jurisdiction_code || null,
    portal_address: metadata.address || null,
    project_name: metadata.project_name || null,
    permit_type: metadata.permit_type || null,
    work_description: Array.isArray(metadata.work_description)
      ? metadata.work_description.join(', ')
      : metadata.work_description || null,
    unit: metadata.unit_number?.toString() || null,
    capacity_unit: metadata.ac_unit_count?.toString() || null,
    capacity: metadata.ac_capacity_in_hp || null,
    question_index: metadata.related_permit_type || 0,
    intent_state: metadata.status || null,
    metadata,
    submit_to_city: false,
  };
}
